/**
 * The five list tabs of Capture V7 (Opportunity, Forecast, Readiness, Denials &
 * Underpayments, Audit Pack) and Contracts › Network share one layout in the design (gen()
 * in capture.js): a KPI strip, a chip row, a table and a rail for the picked row. Each
 * builder here turns one route's answer into that layout. Numbers come from the API only;
 * where the API sends nothing, the cell says so (a dash, "No published rate", an empty
 * state), never a made-up figure.
 */
import type {
  CaptureAppealDraft,
  CaptureAuditRisk,
  CaptureDenial,
  CaptureDenials,
  CaptureEasiestWins,
  CaptureForecast,
  CaptureHorizonRange,
  CaptureQualification,
  CaptureReadiness,
  CaptureSummaryV3,
  CaptureWin,
  QualificationRow,
} from '@aithora/shared';
import { PAYER_NAME } from './model';
import { usd0, usd2, money, moneyC, dayLabel, monthLabel, WHY_BG, type Honesty } from './lib';

export interface GenCell {
  t: string;
  s?: string;
  b?: Honesty | null;
  c?: string;
  w?: string;
  right?: boolean;
  steps?: { t: string; st: string }[];
}
export interface GenRow {
  id: string;
  cells: GenCell[];
  btn: string;
  urgent?: boolean;
}
export interface GenKpi {
  k: string;
  b: Honesty | null;
  v: string;
  s: string;
  vc: string;
}
export interface GenStat {
  k: string;
  b: Honesty | null;
  v: string;
  c: string;
}
export interface GenItem {
  a: string;
  b: string;
  c: string;
  act?: string;
}
export interface GenAct {
  t: string;
  primary: boolean;
  go?: () => void;
  /** Why the button does nothing yet (no route behind it), shown on hover. */
  off?: string | null;
  /** A write: off for a read-only role. */
  write?: boolean;
}
export interface GenRail {
  kicker: string;
  title: string;
  sub: string;
  why: string;
  whyBg: string;
  stats: GenStat[];
  listT: string;
  list: GenItem[];
  draft?: string | null;
  acts: GenAct[];
  foot: string;
}
export interface GenChart {
  band: string;
  lBase: string;
  lScen: string;
  yTop: string;
  yMid: string;
  yBot: string;
  janX: string | null;
  janPct: string | null;
  xL: string;
  xM: string;
  xR: string;
  total: string;
  totalB: Honesty | null;
  sub: string;
}
export interface GenSlider {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  show: string;
  onChange: (v: number) => void;
}
export interface GenData {
  cols: string;
  minW: number;
  kpis: GenKpi[];
  chipLabel: string;
  chips: string[];
  chipOn: (n: string) => boolean;
  onChip: (n: string) => void;
  note: string;
  heads: [string, 'left' | 'right'][];
  rows: GenRow[];
  /** Shown in the table when there are no rows. */
  empty: string;
  rail: GenRail | null;
  foot: string;
  chart?: GenChart | null;
  slider?: GenSlider | null;
}

export interface GenUi {
  filter: string;
  sel: string | null;
  setFilter: (f: string) => void;
  done: Record<string, boolean>;
}

const RED = '#B3261E';
const AMBER = '#6B4A12';
const GREEN = '#1F6B52';
const INK = '#22272E';
const MUTED = '#54606C';

// ── Opportunity (easiest-wins) ───────────────────────────────────────────────
// No 'agreement' kind: a building agreement never blocks billing (the practitioner bills; a
// building is paid only under a signed agreement, 2026-10-20).
type OppKind = 'ready' | 'consent' | 'prac' | 'other';
export interface OppGroup {
  id: string;
  code: string;
  desc: string;
  rateCents: number | null;
  elig: number;
  ready: number;
  yearCents: number | null;
  f: OppKind;
  blk: string;
  act: string;
  wins: CaptureWin[];
  conflicts: number;
}
export const OPP_FILTER: Record<string, OppKind | null> = { All: null, 'Ready now': 'ready', 'Needs consent': 'consent', 'Needs practitioner': 'prac' };
const ACT_OF: Record<OppKind, string> = { ready: 'Start billing', consent: 'Send consent forms', prac: 'Find practitioner', other: 'Open Readiness' };

/** One row per code: how many residents qualify, how many are ready, and the main thing in the way. */
export function oppGroups(w: CaptureEasiestWins | null): OppGroup[] {
  if (!w) return [];
  const by = new Map<string, CaptureWin[]>();
  for (const x of w.wins) by.set(x.codeKey, [...(by.get(x.codeKey) ?? []), x]);
  const out: OppGroup[] = [];
  for (const [key, ws] of by) {
    const residents = new Set(ws.map((x) => x.residentId));
    const ready = new Set(ws.filter((x) => x.blockers.length === 0).map((x) => x.residentId)).size;
    const count = (g: string) => new Set(ws.filter((x) => x.blockers.some((b) => b.gate === g)).map((x) => x.residentId)).size;
    const consent = count('consent');
    const prac = count('practitioner');
    const minutes = count('minutes') + count('device_days');
    const plan = count('care_plan');
    const f: OppKind = prac ? 'prac' : consent ? 'consent' : ready ? 'ready' : 'other';
    const blk =
      f === 'ready'
        ? ready === residents.size ? 'Ready now' : ready + ' ready now'
        : f === 'prac'
          ? 'No billing practitioner linked'
          : f === 'consent'
            ? 'Needs consent from ' + consent + ' resident' + (consent === 1 ? '' : 's')
            : minutes
                ? minutes + ' short on minutes or device days'
                : plan
                  ? 'Needs a care plan (' + plan + ')'
                  : ws[0].blockers[0]?.text ?? '';
    const rates = ws.map((x) => x.monthlyCents).filter((c): c is number => c != null);
    const year = rates.length ? ws.reduce((s, x) => s + (x.monthlyCents ?? 0), 0) * 12 : null;
    out.push({
      id: key,
      code: ws[0].code ?? 'Code hidden',
      desc: ws[0].codeName ?? '',
      rateCents: rates.length ? Math.max(...rates) : null,
      elig: residents.size,
      ready,
      yearCents: year,
      f,
      blk,
      act: ACT_OF[f],
      wins: ws,
      conflicts: w.blocked.filter((b) => b.codeKey === key).length,
    });
  }
  // Easiest money first: ready codes, then the most dollars a year.
  return out.sort((a, b) => Number(b.f === 'ready') - Number(a.f === 'ready') || (b.yearCents ?? -1) - (a.yearCents ?? -1));
}

export function opportunity(w: CaptureEasiestWins | null, ui: GenUi, act: { start: (g: OppGroup) => void; ask: () => void }, tagSuffix: string): GenData {
  const all = oppGroups(w);
  const want = OPP_FILTER[ui.filter] ?? null;
  const list = all.filter((g) => !want || g.f === want);
  const sel = all.find((g) => g.id === ui.sel) ?? list[0] ?? null;
  const tot = all.reduce((s, g) => s + (g.yearCents ?? 0), 0);
  const withRate = all.filter((g) => g.rateCents != null).length;
  const mb: Honesty | null = w ? (w.real ? 'MODELED' : 'SAMPLE') : null;
  const meas: Honesty | null = w ? (w.real ? 'MEASURED' : 'SAMPLE') : null;
  const pub: Honesty | null = w ? (w.real ? 'PUBLISHED' : 'SAMPLE') : null;
  const where = 'PFS 2026 · ' + (w?.locality ? w.locality.name : 'national');
  const residents = new Set((w?.wins ?? []).map((x) => x.residentId)).size;
  const readyCodes = all.filter((g) => g.f === 'ready').length;
  return {
    cols: 'minmax(0,1.5fr) 90px 64px 96px 70px minmax(0,1.3fr) 128px',
    minW: 660,
    kpis: [
      { k: 'Left on the table', b: mb, v: w ? money(tot / 100) + ' / yr' : '–', s: 'codes you qualify for, not billed', vc: AMBER },
      { k: 'Codes to start', b: meas, v: String(all.length), s: readyCodes + ' ready now', vc: INK },
      { k: 'Residents who qualify', b: meas, v: String(residents), s: 'from care records and Oversight', vc: INK },
      { k: 'Rates loaded', b: pub, v: withRate + ' of ' + all.length, s: all.length - withRate ? all.length - withRate + ' codes still to load' : 'every code has a rate', vc: INK },
    ],
    chipLabel: 'Show',
    chips: ['All', 'Ready now', 'Needs consent', 'Needs practitioner'],
    chipOn: (n) => (ui.filter || 'All') === n,
    onChip: ui.setFilter,
    note: 'Easiest money first · same-month conflicts checked before anything is suggested',
    heads: [['Code', 'left'], ['Pays / res / mo', 'right'], ['Qualify', 'right'], ['Per year', 'right'], ['Fit', 'left'], ['What is in the way', 'left'], ['', 'right']],
    rows: list.map((g) => ({
      id: g.id,
      cells: [
        { t: g.code, s: g.desc, w: '700' },
        { t: g.rateCents != null ? usd2(g.rateCents) : 'No published rate', s: g.rateCents != null ? where : 'rate not loaded', c: g.rateCents != null ? INK : MUTED, right: true },
        { t: String(g.elig), right: true },
        { t: g.yearCents ? usd0(g.yearCents) : 'not shown', w: '700', c: g.yearCents ? AMBER : MUTED, b: g.yearCents ? mb : null, right: true },
        { t: g.ready + ' ready', w: '700', c: g.ready ? GREEN : AMBER },
        { t: g.blk, c: g.f === 'ready' ? GREEN : INK },
      ],
      btn: ui.done[g.id] ? 'Started ✓' : g.act,
    })),
    empty: w ? (w.wins.length ? 'Nothing matches this filter.' : 'No unbilled codes this month. Every qualifying resident is billed or held.') : '',
    rail: sel
      ? {
          kicker: 'WHY THIS CODE',
          title: sel.code + (sel.desc ? ' · ' + sel.desc : ''),
          sub: sel.elig + ' residents qualify' + (sel.rateCents != null ? ' · ' + usd2(sel.rateCents) + ' per resident per month' : ' · No published rate'),
          why: sel.wins[0]?.reason ? sel.wins[0].reason[0].toUpperCase() + sel.wins[0].reason.slice(1) + '.' : '',
          whyBg: 'rgba(107,74,18,.08)',
          stats: [
            { k: 'Per year', b: mb, v: sel.yearCents ? usd0(sel.yearCents) : 'No published rate', c: AMBER },
            { k: 'Qualify', b: meas, v: String(sel.elig), c: INK },
            { k: 'Ready now', b: meas, v: String(sel.ready), c: sel.ready ? GREEN : INK },
            { k: 'Same-month conflicts', b: meas, v: sel.conflicts ? sel.conflicts + ' held' : 'None', c: sel.conflicts ? AMBER : GREEN },
          ],
          listT: 'Residents who qualify · summary from the care record',
          list: sel.wins.slice(0, 4).map((x) => ({ a: x.resident, b: x.blockers.length ? x.blockers.map((b) => b.text).join(', ') : 'nothing missing: bills when the month closes', c: INK, act: 'Save' })),
          acts: [
            { t: ui.done[sel.id] ? 'Started ✓' : sel.act, primary: true, go: () => act.start(sel), write: sel.f !== 'other' },
            { t: 'Ask Atlas', primary: false, go: act.ask },
          ],
          foot: 'Residents shown as a summary. Full words from Atlas conversations stay in the record, for staff with access.',
        }
      : null,
    foot: list.length + ' codes · the enrolled practitioner bills · documentation support by Aithora' + tagSuffix,
  };
}

// ── Readiness (readiness + qualification + the summary's pipeline) ───────────
export const STEPS = ['Consent', 'Care plan', 'Diagnosis', 'Minutes', 'Practitioner sign-off', 'Sent', 'Accepted', 'Paid'];
const stepStyle = (i: number, at: number) =>
  'width:10px;height:10px;border-radius:3px;' +
  (i < at ? 'background:#1F6B52;' : i === at && at < 7 ? 'background:#FCFAF4;box-shadow:inset 0 0 0 2px ' + (at < 5 ? '#B3261E' : '#2F5C97') + ';' : i === 7 && at === 7 ? 'background:#1F6B52;' : 'background:rgba(34,39,46,.12);');

export interface ReadyRow {
  id: string;
  who: string;
  sub: string;
  code: string;
  at: number;
  blk: string;
  clock: string;
  urgent: boolean;
  cents: number;
  fix: string | null;
  fixAction: string | null;
  claimId: string | null;
  residentId: string;
  practitioner: string | null;
}

export function readyRows(r: CaptureReadiness | null, q: CaptureQualification | null, now: number): ReadyRow[] {
  if (!r) return [];
  const closes = q ? Math.max(0, Math.ceil((Date.parse(q.closesOn + 'T00:00:00') - now) / 86_400_000)) : null;
  return r.rows.map((x) => {
    const qr = q?.residents.find((y) => y.residentId === x.residentId);
    const open: QualificationRow | undefined = qr?.rows.find((y) => !y.qualified) ?? qr?.rows[0];
    const dx = !!qr?.rows.some((y) => y.stillNeeded.some((s) => s.gate === 'chronic_conditions'));
    const at = !x.gates.consent ? 0 : !x.gates.carePlan ? 1 : dx ? 2 : !x.gates.time ? 3 : !x.gates.practitioner || !x.gates.visit ? 4 : 5;
    const blk =
      at === 0 ? (x.consentProgram ? 'Needs ' + x.consentProgram + ' consent' : 'Needs consent')
        : at === 1 ? 'No care plan'
          : at === 2 ? 'Diagnosis missing'
            : at === 3 ? (open?.minutes ? open.minutes.have + ' of ' + open.minutes.need + ' min' : open?.deviceDays ? open.deviceDays.have + ' of ' + open.deviceDays.need + ' days' : 'Minutes short')
              : at === 4 ? (!x.gates.visit ? 'Needs a visit' : 'Practitioner to sign')
                : 'Ready to send';
    const short = open?.minutes ? open.minutes.need - open.minutes.have : 0;
    const clock =
      at === 0 ? 'Consent missing'
        : at === 3 && closes != null ? (short > 0 ? short + ' min short · ' + closes + ' d' : closes + ' d to month end')
          : at < 5 && closes != null ? closes + ' d to month end'
            : '–';
    return {
      id: x.residentId,
      residentId: x.residentId,
      who: x.resident + (x.room ? ' · ' + x.room.replace(/^Rm /, '') : ''),
      sub: x.facilityName ?? '',
      code: open?.code ?? '–',
      at,
      blk,
      clock,
      urgent: at === 0,
      cents: x.blockedCents,
      fix: x.fix,
      fixAction: x.fixAction,
      claimId: x.claimId,
      practitioner: open?.practitioner?.name ?? null,
    };
  });
}

export function readiness(
  r: CaptureReadiness | null,
  q: CaptureQualification | null,
  s: CaptureSummaryV3 | null,
  ui: GenUi,
  now: number,
  act: { fix: (row: ReadyRow) => void; sendAll: () => void; consentCount: number },
  tagSuffix: string,
): GenData {
  const all = readyRows(r, q, now);
  const f = ui.filter || 'All';
  const list = all.filter((x) => f === 'All' || (f === 'Blocked' ? x.at < 5 : f === 'At clearinghouse' ? x.at === 5 || x.at === 6 : x.at === 7));
  const sel = all.find((x) => x.id === ui.sel) ?? list[0] ?? null;
  const st = s?.pipeline?.stages ?? [];
  const n = (k: string) => st.find((x) => x.key === k)?.count ?? 0;
  const claims = st.reduce((a, x) => a + x.count, 0);
  const blocked = all.filter((x) => x.at < 5);
  const meas: Honesty | null = r ? (r.real ? 'MEASURED' : 'SAMPLE') : null;
  const pipeB: Honesty | null = s?.pipeline ? (s.pipeline.basis === 'sample' || !s.real ? 'SAMPLE' : 'MEASURED') : null;
  const closes = q ? Math.max(0, Math.ceil((Date.parse(q.closesOn + 'T00:00:00') - now) / 86_400_000)) : null;
  return {
    // Sized so each row stays at two lines at 1280: the stepper column is the stepper's own
    // width (8 x 10px + 7 x 3px) and the button column its widest button's.
    cols: 'minmax(0,1.6fr) 44px 101px minmax(0,1.1fr) minmax(0,.65fr) 44px 102px',
    minW: 660,
    kpis: [
      { k: 'Claims this month', b: pipeB, v: s?.pipeline ? String(claims) : '–', s: 'all codes, all wings', vc: INK },
      { k: 'Ready to send', b: pipeB, v: s?.pipeline ? String(n('ready')) : '–', s: claims ? Math.round((n('ready') / claims) * 100) + '%' : '', vc: GREEN },
      { k: 'Blocked', b: meas, v: String(blocked.length), s: usd0(blocked.reduce((a, x) => a + x.cents, 0)) + ' this month', vc: RED },
      { k: 'At clearinghouse', b: pipeB, v: s?.pipeline ? String(n('submitted')) : '–', s: 'sent, waiting on the payer', vc: INK },
      { k: 'Month closes in', b: q ? (q.real ? 'MEASURED' : 'SAMPLE') : null, v: closes != null ? closes + ' d' : '–', s: q ? 'minutes must be logged by ' + dayLabel(q.closesOn) : 'choose one building', vc: AMBER },
    ],
    chipLabel: 'Show',
    chips: ['All', 'Blocked', 'At clearinghouse', 'Paid'],
    chipOn: (c) => f === c,
    onChip: ui.setFilter,
    note: 'Green done · outlined is the current step · red is blocked',
    heads: [['Resident', 'left'], ['Code', 'left'], ['Consent → Paid', 'left'], ['Now', 'left'], ['Clock', 'left'], ['Value', 'right'], ['', 'right']],
    rows: list.map((x) => ({
      id: x.id,
      cells: [
        { t: x.who, s: x.sub || undefined, w: '700' },
        { t: x.code, w: '700' },
        { t: '', steps: STEPS.map((nm, i) => ({ t: nm, st: stepStyle(i, x.at) })) },
        // The step's name under the blocker only when the blocker does not already say it.
        { t: x.blk, c: x.at < 5 ? RED : x.at === 7 ? GREEN : INK, w: '700', s: x.blk.toLowerCase().includes(STEPS[Math.min(x.at, 7)].toLowerCase()) ? undefined : STEPS[Math.min(x.at, 7)] },
        { t: x.clock, c: x.urgent ? RED : '#3C454F' },
        { t: usd0(x.cents), right: true },
      ],
      btn: ui.done[x.id] ? 'Done ✓' : x.fix ?? 'View',
      urgent: x.urgent,
    })),
    empty: f === 'At clearinghouse' || f === 'Paid' ? 'Claims after sending are not on this list yet: it shows what each claim still needs.' : r ? 'Nothing blocked. Every resident in scope has what a claim needs.' : '',
    rail: sel
      ? {
          kicker: 'CLAIM CHECKLIST',
          title: sel.who,
          sub: [sel.code, sel.sub, usd0(sel.cents) + ' this month'].filter((v) => v && v !== '–').join(' · '),
          why: sel.at >= 5 ? 'Ready to send. Nothing to do.' : 'Stuck at ' + STEPS[sel.at].toLowerCase() + ': ' + sel.blk.charAt(0).toLowerCase() + sel.blk.slice(1) + '.',
          whyBg: sel.at < 5 ? 'rgba(179,38,30,.07)' : 'rgba(47,92,151,.08)',
          stats: [
            { k: 'Practitioner', b: meas, v: sel.practitioner ?? 'None', c: sel.practitioner ? INK : RED },
            { k: 'Panel load', b: null, v: '–', c: INK },
          ],
          listT: 'Steps',
          list: STEPS.map((nm, i) => ({
            a: (i < sel.at ? '✓ ' : i === sel.at ? '→ ' : '') + nm,
            b: i < sel.at ? 'Done' : i === sel.at ? sel.blk : 'Waiting',
            c: i < sel.at ? GREEN : i === sel.at ? (sel.at < 5 ? RED : '#2F5C97') : MUTED,
          })),
          acts: [
            { t: ui.done[sel.id] ? 'Done ✓' : sel.fix ?? 'View', primary: true, go: () => act.fix(sel), write: !!sel.fix },
            { t: 'Send all consent forms (' + act.consentCount + ')', primary: false, go: act.sendAll, write: true, off: act.consentCount ? null : 'No consent links to send: no family contact is on file yet.' },
          ],
          foot: 'Practitioners live here: panel load, agreements, and gaps like Wing C. Residents in a Medicare Part A skilled stay are held automatically: CCM can’t be billed during the stay, but time outside it still counts toward the month (CMS CCM FAQ).',
        }
      : null,
    foot: list.length + ' claims shown of ' + (s?.pipeline ? claims : all.length) + tagSuffix,
  };
}

// ── Denials & Underpayments ──────────────────────────────────────────────────
const carcOf = (d: CaptureDenial) => d.reasons.map((r) => (r.group ? r.group + '-' : '') + r.code).join(', ');
const chanceOf = (d: CaptureDenial) => (d.recovery.score != null ? Math.round(d.recovery.score) : null);
const deadlineOf = (d: CaptureDenial) =>
  d.daysLeft != null ? (d.daysLeft < 0 ? 'Too late · ' + -d.daysLeft + ' days' : d.daysLeft + ' d to ' + (d.action === 'appeal' ? 'appeal' : 'fix')) : d.appealable ? (d.deadlines.text ?? 'Check the plan’s deadline') : 'No appeal · fix and close';
const ACTION_BTN: Record<CaptureDenial['action'], string> = { appeal: 'Draft appeal', correct_and_resubmit: 'Fix and resubmit', rebill_other_payer: 'Rebill other payer', none: 'Close' };

export function denials(
  d: CaptureDenials | null,
  draft: CaptureAppealDraft | null,
  ui: GenUi,
  act: { review: (x: CaptureDenial) => void; edit: () => void },
  tagSuffix: string,
): GenData {
  const all = (d?.denials ?? []).slice().sort((a, b) => (chanceOf(b) ?? 0) * b.deniedCents - (chanceOf(a) ?? 0) * a.deniedCents);
  const f = ui.filter || 'All';
  const list = f === 'Underpaid' ? [] : all;
  const sel = all.find((x) => x.id === ui.sel) ?? list[0] ?? null;
  const meas: Honesty | null = d ? (d.real ? 'MEASURED' : 'SAMPLE') : null;
  const mb: Honesty | null = d ? (d.real ? 'MODELED' : 'SAMPLE') : null;
  const appealable = all.filter((x) => x.appealable);
  const recoverable = all.reduce((s, x) => s + ((chanceOf(x) ?? 0) / 100) * x.deniedCents, 0);
  const near = all.filter((x) => x.daysLeft != null && x.daysLeft >= 0).sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0))[0];
  const who = (x: CaptureDenial) => (x.payerName ?? PAYER_NAME[x.payerClass]) + ' · ' + (x.resident ?? 'Resident');
  const draftText = draft
    ? [
        draft.request.basis + '. To: ' + draft.request.to + '.',
        'Claim ' + (draft.claim.claimRef ?? draft.claim.denialId) + (draft.claim.code ? ' (' + draft.claim.code + ')' : '') + ', denied ' + usd2(draft.claim.deniedCents) + ': ' + (draft.denial.reasons[0]?.plain ?? draft.denial.reasons[0]?.reason ?? '') + '.',
        draft.denial.fix,
        draft.notOnFile.length ? 'Not on file: ' + draft.notOnFile.join(', ') + '.' : '',
      ].filter(Boolean).join(' ')
    : null;
  return {
    cols: 'minmax(0,1.3fr) 58px minmax(0,1.5fr) 70px 70px minmax(0,1fr) 110px',
    minW: 660,
    kpis: [
      { k: 'Denied, open', b: meas, v: String(appealable.length), s: usd0(appealable.reduce((s, x) => s + x.deniedCents, 0)) + ' appealable', vc: RED },
      { k: 'Underpaid', b: null, v: '–', s: 'needs your contract rates on file', vc: RED },
      { k: 'Recoverable', b: mb, v: usd0(recoverable), s: 'chance × dollars', vc: AMBER },
      { k: 'Nearest deadline', b: meas, v: near ? near.daysLeft + ' d' : '–', s: near ? who(near) : 'no open Medicare deadline', vc: RED },
      { k: 'Recovered this year', b: null, v: '–', s: 'appeal outcomes are not tracked yet', vc: GREEN },
    ],
    chipLabel: 'Show',
    chips: ['All', 'Denied', 'Underpaid'],
    chipOn: (c) => f === c,
    onChip: ui.setFilter,
    note: 'Ranked by chance of getting paid × dollars · underpaid = remittance below your contract rate',
    heads: [['Payer · resident', 'left'], ['Code', 'left'], ['Why, in plain words', 'left'], ['At stake', 'right'], ['Chance', 'right'], ['Deadline', 'left'], ['', 'right']],
    rows: list.map((x) => {
      const ch = chanceOf(x);
      const urgent = x.daysLeft != null && x.daysLeft >= 0 && x.daysLeft <= 14;
      return {
        id: x.id,
        cells: [
          { t: who(x), s: 'Denied', w: '700' },
          { t: x.code ?? '–', w: '700' },
          { t: x.reasons[0]?.plain ?? x.reasons[0]?.reason ?? x.fix, s: carcOf(x) || undefined },
          { t: usd2(x.deniedCents), w: '700', right: true },
          { t: ch != null ? ch + '%' : x.recovery.rank.replace('_', ' '), w: '700', c: ch == null ? MUTED : ch >= 75 ? GREEN : ch >= 40 ? AMBER : MUTED, b: mb, right: true },
          { t: deadlineOf(x), c: urgent ? RED : '#3C454F', w: urgent ? '700' : '500' },
        ],
        btn: ui.done[x.id] ? 'Sent ✓' : ACTION_BTN[x.action],
        urgent,
      };
    }),
    empty: f === 'Underpaid' ? 'Underpayments need your contract rates: upload a contract or remittance in Contracts.' : d ? 'No denials on file for this building.' : '',
    rail: sel
      ? {
          kicker: 'APPEAL',
          title: who(sel) + (sel.code ? ' · ' + sel.code : ''),
          sub: [carcOf(sel), deadlineOf(sel)].filter(Boolean).join(' · '),
          why: (sel.reasons[0]?.plain ?? sel.reasons[0]?.reason ?? '') + (sel.reasons.length ? '. ' : '') + sel.fix,
          whyBg: 'rgba(179,38,30,.07)',
          stats: [
            { k: 'At stake', b: meas, v: usd2(sel.deniedCents), c: INK },
            { k: 'Chance of payment', b: mb, v: chanceOf(sel) != null ? chanceOf(sel) + '%' : sel.recovery.rank.replace('_', ' '), c: (chanceOf(sel) ?? 0) >= 75 ? GREEN : AMBER },
          ],
          listT: 'Evidence attached',
          list: [
            { a: 'Signed consent', b: sel.evidence.consent ? 'on file' : 'not on file', c: sel.evidence.consent ? INK : RED, act: 'View' },
            { a: 'Time log', b: sel.evidence.minutes ? 'on file' : 'not on file', c: sel.evidence.minutes ? INK : RED, act: 'View' },
            { a: 'Care plan', b: sel.evidence.carePlan ? 'on file' : 'not on file', c: sel.evidence.carePlan ? INK : RED, act: 'View' },
          ],
          draft: sel.appealable ? draftText ?? 'Loading the draft…' : null,
          acts: [
            { t: ui.done[sel.id] ? 'Sent ✓' : 'Review and send appeal', primary: true, go: () => act.review(sel), off: sel.appealable ? null : 'This denial cannot be appealed.' },
            { t: 'Edit draft', primary: false, go: act.edit, off: sel.appealable ? null : 'No draft for this denial.' },
          ],
          foot: 'Atlas drafts. A person reviews and sends. Patterns by payer feed the Contracts brief.',
        }
      : null,
    foot: list.length + ' items · reason codes are standard CARC codes' + tagSuffix,
  };
}

// ── Audit Pack (audit-risk) ──────────────────────────────────────────────────
export interface AuditFlag {
  id: string;
  code: string;
  pat: string;
  norm: string;
  published: boolean;
  risk: 'High' | 'Medium' | 'Low';
  flag: string;
  fix: string;
  who: string;
  residents: number;
  months: number;
}
const fmtMeasure = (key: string, v: number | null) => (v == null ? '–' : key === 'eligible_share_billed' || v < 1 ? Math.round(v * 100) + '%' : v.toFixed(1));

export function auditFlags(a: CaptureAuditRisk | null): AuditFlag[] {
  if (!a) return [];
  const out: AuditFlag[] = [];
  for (const p of a.practitioners) {
    for (const m of p.measures) {
      const risk = m.points >= 2 ? 'High' : m.points === 1 ? 'Medium' : 'Low';
      out.push({
        id: p.practitionerId + ':' + (p.code ?? '') + ':' + m.key,
        code: p.code ?? '–',
        pat: m.label + ': ' + fmtMeasure(m.key, m.value),
        norm: m.comparison === 'compared' && m.national != null ? fmtMeasure(m.key, m.national) : m.comparison === 'no_national_norm' ? 'No public benchmark' : 'Too few claims',
        published: m.comparison === 'compared' && m.national != null,
        risk,
        flag: m.why ?? (risk === 'Low' ? 'No issues' : m.label),
        fix: risk === 'High' ? 'Review claims' : risk === 'Medium' ? 'Open time logs' : 'Export',
        who: p.practitioner ?? (p.npi ? 'NPI ' + p.npi : 'Practitioner'),
        residents: p.residents,
        months: p.months,
      });
    }
  }
  const rank = { High: 0, Medium: 1, Low: 2 };
  return out.sort((x, y) => rank[x.risk] - rank[y.risk]);
}

export function auditPack(a: CaptureAuditRisk | null, missingConsent: number | null, ui: GenUi, act: { fix: (f: AuditFlag) => void }, tagSuffix: string): GenData {
  const all = auditFlags(a);
  const f = ui.filter || 'All';
  const list = all.filter((x) => f === 'All' || x.risk === f);
  const sel = all.find((x) => x.id === ui.sel) ?? list[0] ?? null;
  const mb: Honesty | null = a ? (a.real ? 'MODELED' : 'SAMPLE') : null;
  const pub: Honesty | null = a ? (a.real ? 'PUBLISHED' : 'SAMPLE') : null;
  const hi = all.filter((x) => x.risk === 'High').length;
  const med = all.filter((x) => x.risk === 'Medium').length;
  const worst = hi ? 'High' : med ? 'Medium' : all.length ? 'Low' : '–';
  const rc = (r: string) => (r === 'High' ? RED : r === 'Medium' ? AMBER : GREEN);
  return {
    cols: '64px minmax(0,1.5fr) 96px 70px minmax(0,1.3fr) 146px',
    minW: 660,
    kpis: [
      { k: 'Audit risk', b: mb, v: worst, s: hi + ' high flags · ' + med + ' medium', vc: worst === '–' ? INK : rc(worst) },
      { k: 'Claims ready to defend', b: null, v: '–', s: 'open a claim for its evidence pack', vc: GREEN },
      { k: 'Missing consent', b: missingConsent == null ? null : 'MEASURED', v: missingConsent == null ? '–' : String(missingConsent), s: 'fix before anyone asks', vc: RED },
      { k: 'Last export', b: null, v: '–', s: 'no bundle exported yet', vc: INK },
    ],
    chipLabel: 'Show',
    chips: ['All', 'High', 'Medium', 'Low'],
    chipOn: (c) => f === c,
    onChip: ui.setFilter,
    note: 'What an auditor would question, against published Medicare figures where one exists',
    heads: [['Code', 'left'], ['Your pattern', 'left'], ['Compared with', 'right'], ['Risk', 'left'], ['What an auditor would ask', 'left'], ['', 'right']],
    rows: list.map((x) => ({
      id: x.id,
      cells: [
        { t: x.code, w: '700' },
        { t: x.pat, s: x.who },
        { t: x.norm, b: x.published ? pub : null, c: x.published || x.norm === '–' ? INK : MUTED, right: true },
        { t: x.risk, w: '700', c: rc(x.risk) },
        { t: x.flag },
      ],
      btn: ui.done[x.id] ? 'Fixed ✓' : x.fix,
      urgent: x.risk === 'High',
    })),
    empty: a ? 'No billing patterns to compare yet: the building has no billed months in the window.' : '',
    rail: sel
      ? {
          kicker: 'AUDIT FLAG',
          title: sel.code + ' · ' + sel.flag,
          sub: sel.who + ' · ' + sel.residents + ' residents · ' + sel.months + ' months',
          why:
            'Your pattern: ' + sel.pat.charAt(0).toLowerCase() + sel.pat.slice(1) +
            (sel.published ? '. Published comparison: ' + sel.norm + (a ? ' (' + a.source.name + ', ' + a.source.version + ')' : '') + '.' : sel.norm === 'No public benchmark' ? '. There is no public benchmark for this pattern, so nothing is compared.' : '. Too few claims to compare.'),
          whyBg: sel.risk === 'High' ? 'rgba(179,38,30,.07)' : sel.risk === 'Medium' ? 'rgba(107,74,18,.08)' : 'rgba(31,107,82,.08)',
          stats: [
            { k: 'Risk', b: mb, v: sel.risk, c: rc(sel.risk) },
            { k: 'Residents', b: mb ? (a?.real ? 'MEASURED' : 'SAMPLE') : null, v: String(sel.residents), c: INK },
          ],
          listT: 'Evidence per claim',
          list: [
            { a: 'Consent', b: 'signed, dated, on file', c: INK },
            { a: 'Time log', b: 'who, when, minutes', c: INK },
            { a: 'Care plan', b: 'current and signed', c: INK },
            { a: 'Practitioner sign-off', b: 'NPI and date', c: INK },
          ],
          acts: [
            { t: ui.done[sel.id] ? 'Fixed ✓' : sel.fix, primary: true, go: () => act.fix(sel) },
            { t: 'Export audit bundle (PDF)', primary: false, off: 'The PDF bundle is not built yet. Each claim’s evidence pack opens from the claim.' },
          ],
          foot: 'Facility Admins see their own building. Comparisons use published Medicare figures; where none exists, the row says so.',
        }
      : null,
    foot: list.length + ' flags' + tagSuffix,
  };
}

// ── Forecast (forecast horizon) ──────────────────────────────────────────────
const PRESET_LABEL: Record<string, string> = { cautious: 'Cautious', expected: 'Expected', strong: 'Strong' };

/** Monthly points from the 3, 6 and 12 month totals: each stretch's total spread over its months. */
export function monthlyFrom(h: CaptureHorizonRange[] | undefined, pick: 'low' | 'expected' | 'high'): number[] {
  if (!h?.length) return [];
  const t = (m: number) => h.find((x) => x.months === m)?.[pick].practitioner.value ?? null;
  const t3 = t(3);
  const t6 = t(6);
  const t12 = t(12);
  if (t3 == null || t6 == null || t12 == null) return [];
  return [...Array(3).fill(t3 / 3), ...Array(3).fill((t6 - t3) / 3), ...Array(6).fill((t12 - t6) / 6)];
}

export function forecast(
  fc: CaptureForecast | null,
  base: CaptureForecast | null,
  ui: GenUi & { uptake: number | null; setUptake: (v: number) => void },
  act: { pick: (k: string) => void; exportIt: () => void },
  tagSuffix: string,
): GenData {
  const hz = fc?.horizon ?? null;
  const bz = base?.horizon ?? hz;
  const h12 = hz?.horizons.find((x) => x.months === 12) ?? null;
  const b12 = bz?.horizons.find((x) => x.months === 12) ?? null;
  const mb: Honesty | null = fc ? (fc.real ? 'MODELED' : 'SAMPLE') : null;
  const scen = monthlyFrom(hz?.horizons, 'expected');
  const lo = monthlyFrom(hz?.horizons, 'low');
  const hi = monthlyFrom(hz?.horizons, 'high');
  const cur = monthlyFrom(bz?.horizons, 'expected');
  let chart: GenChart | null = null;
  if (scen.length === 12 && lo.length === 12 && hi.length === 12) {
    const mx = Math.max(...hi, ...cur) * 1.02;
    const mn = Math.min(...lo, ...cur) * 0.98;
    const X = (i: number) => ((i / 11) * 600).toFixed(1);
    const Y = (v: number) => (150 - ((v - mn) / (mx - mn || 1)) * 146 - 2).toFixed(1);
    const pl = (a: number[]) => a.map((v, i) => X(i) + ',' + Y(v)).join(' ');
    const band = hi.map((v, i) => X(i) + ',' + Y(v)).concat(lo.slice().reverse().map((v, i) => X(11 - i) + ',' + Y(v))).join(' ');
    const from = h12?.from ?? '';
    // The month the next calendar year starts, where 2027 rates would apply.
    const jan = from ? (13 - Number(from.slice(5, 7))) % 12 : -1;
    const tot = h12?.expected.practitioner.value ?? 0;
    const totB = b12?.expected.practitioner.value ?? 0;
    chart = {
      band,
      lBase: pl(cur),
      lScen: pl(scen),
      yTop: moneyC(mx) + '/mo',
      yMid: moneyC((mx + mn) / 2),
      yBot: moneyC(mn),
      // The 2027 proposed rates are not loaded, so no line marks where they would start.
      janX: null,
      janPct: null,
      xL: from ? monthLabel(from.slice(0, 7)) : '',
      xM: jan > 0 && from ? 'Jan ' + (Number(from.slice(0, 4)) + 1) : '',
      xR: h12 ? monthLabel(h12.through.slice(0, 7)) : '',
      total: moneyC(tot),
      totalB: mb,
      sub: (tot - totB >= 0 ? '+' : '−') + moneyC(Math.abs(tot - totB)) + ' over the current path',
    };
  }
  const presets = hz?.presets ?? [];
  const selP = presets.find((p) => p.key === ui.sel) ?? presets.find((p) => p.key === hz?.scenario) ?? presets[0] ?? null;
  const p12 = (p: (typeof presets)[number]) => p.horizons.find((x) => x.months === 12)?.expected.practitioner.value ?? 0;
  const baseYear = b12?.expected.practitioner.value ?? 0;
  const upt = hz?.assumptions.find((x) => x.key === 'uptake') ?? null;
  const pct = (v: number) => Math.round(v * 100);
  return {
    cols: 'minmax(0,1.6fr) 110px minmax(0,1.3fr) 90px 110px',
    minW: 640,
    kpis: [
      { k: 'Billed, next 12 months', b: mb, v: h12 ? moneyC(h12.expected.practitioner.value ?? 0) : '–', s: 'with the scenario you picked', vc: INK },
      { k: 'Current path', b: mb, v: b12 ? moneyC(b12.expected.practitioner.value ?? 0) : '–', s: 'if nothing changes', vc: INK },
      { k: '2027 proposed rates', b: null, v: '–', s: 'proposed 2027 rates not loaded yet', vc: RED },
      { k: 'Scenarios on', b: mb, v: selP ? '1' : '0', s: upt ? 'at ' + pct(upt.value) + '% uptake' : '', vc: INK },
    ],
    chipLabel: 'Scenarios',
    chips: presets.map((p) => PRESET_LABEL[p.key] ?? p.label),
    chipOn: (n) => (selP ? (PRESET_LABEL[selP.key] ?? selP.label) === n : false),
    onChip: (n) => {
      const p = presets.find((x) => (PRESET_LABEL[x.key] ?? x.label) === n);
      if (p) act.pick(p.key);
    },
    slider: upt
      ? {
          label: 'How many succeed',
          min: pct(upt.min),
          max: pct(upt.max),
          step: Math.max(1, pct(upt.step)),
          value: ui.uptake ?? pct(upt.value),
          show: (ui.uptake ?? pct(upt.value)) + '%',
          onChange: ui.setUptake,
        }
      : null,
    note: '',
    chart,
    heads: [['Scenario', 'left'], ['Adds per month', 'right'], ['What it needs', 'left'], ['Starts', 'left'], ['', 'right']],
    rows: presets.map((p) => {
      const add = (p12(p) - baseYear) / 12;
      const on = selP?.key === p.key;
      return {
        id: p.key,
        cells: [
          { t: PRESET_LABEL[p.key] ?? p.label, w: '700' },
          { t: (add >= 0 ? '+' : '−') + usd0(Math.abs(add)), w: '700', c: add >= 0 ? GREEN : RED, b: mb, right: true },
          { t: p.definition },
          { t: h12 ? monthLabel(h12.from.slice(0, 7)) : '–' },
        ],
        btn: on ? 'In forecast' : 'Add',
      };
    }),
    empty: fc ? fc.horizonReason ?? 'No forecast for this scope.' : '',
    rail: selP
      ? {
          kicker: 'SCENARIO',
          title: PRESET_LABEL[selP.key] ?? selP.label,
          sub: 'Adds about ' + usd0(Math.abs((p12(selP) - baseYear) / 12)) + ' a month against the current path',
          why: selP.definition + ' Every scenario is a model. ' + (hz?.rangeRule ?? ''),
          whyBg: 'rgba(107,74,18,.08)',
          stats: [
            { k: 'Per month', b: mb, v: usd0(p12(selP) / 12), c: GREEN },
            { k: 'Per year', b: mb, v: usd0(p12(selP)), c: GREEN },
          ],
          listT: 'What it assumes',
          list: (hz?.assumptions ?? []).map((x) => ({ a: x.label + ': ' + (x.key === 'census' ? String(Math.round(selP.values[x.key] ?? x.value)) : pct(selP.values[x.key] ?? x.value) + '%'), b: x.source, c: INK })),
          acts: [
            { t: selP.key === hz?.scenario ? 'Remove from forecast' : 'Add to forecast', primary: true, go: () => act.pick(selP.key === hz?.scenario ? 'expected' : selP.key) },
            { t: 'Export forecast', primary: false, go: act.exportIt },
          ],
          foot: (hz?.formula ?? '') + (hz?.measured.source ? ' Months already paid: ' + hz.measured.source + '.' : ''),
        }
      : null,
    foot: 'Forecast is modeled from your last 12 months' + tagSuffix,
  };
}

// ── Contracts › Network ──────────────────────────────────────────────────────
export function network(ui: GenUi): GenData {
  return {
    cols: 'minmax(0,1.6fr) 64px 46px 60px 66px 76px minmax(0,1fr) 112px',
    minW: 660,
    kpis: [
      { k: 'Network status', b: null, v: 'Not started', s: 'antitrust counsel designs the structure first', vc: AMBER },
      { k: 'Member buildings', b: null, v: '–', s: 'no network on file', vc: INK },
      { k: 'Almost ready', b: null, v: '–', s: 'no checklist sent', vc: AMBER },
      { k: 'Medicare Advantage members', b: null, v: '–', s: 'across member buildings', vc: INK },
      { k: 'Rates shared between owners', b: null, v: '0', s: 'never, by design', vc: GREEN },
    ],
    chipLabel: 'Show',
    chips: ['All', 'Members', 'Almost ready', 'Not ready'],
    chipOn: (c) => (ui.filter || 'All') === c,
    onChip: ui.setFilter,
    note: 'Owners keep their buildings. Aithora is the network. Joint contracting starts only after counsel signs off.',
    heads: [['Building · owner', 'left'], ['Ready', 'right'], ['Stars', 'left'], ['Readmit', 'right'], ['Protocols', 'left'], ['Audit', 'left'], ['Status', 'left'], ['', 'right']],
    rows: [],
    empty: 'No network yet. Buildings join once antitrust counsel has designed the structure; owners never see each other’s rates.',
    rail: null,
    foot: '0 buildings · no member rates shown here',
  };
}

export { WHY_BG };
